import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { ButtonSpinner } from '../common/Loader';
import { parseError } from '../../utils/errors';
import { formatUsdc, parseUsdc } from '../../utils/format';

export const PriceControl = () => {
  const { storageR, adminW } = useContracts();
  const notify = useNotify();

  const [price, setPrice] = useState(null);
  const [min, setMin] = useState(null);
  const [max, setMax] = useState(null);
  const [claimMode, setClaimMode] = useState(false);

  const [newPrice, setNewPrice] = useState('');
  const [newMin, setNewMin] = useState('');
  const [newMax, setNewMax] = useState('');

  const [busy, setBusy] = useState(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!storageR) return;
      try {
        const [p, mn, mx, cm] = await Promise.all([
          storageR.zenovaPricePerUsdc(),
          storageR.zenovaPriceMin(),
          storageR.zenovaPriceMax(),
          storageR.usdcClaimMode()
        ]);
        if (cancelled) return;
        setPrice(p);
        setMin(mn);
        setMax(mx);
        setClaimMode(cm);
      } catch (e) {}
    };
    load();
    return () => { cancelled = true; };
  }, [storageR, reload]);

  const runTx = async (label, fn) => {
    setBusy(label);
    try {
      const tx = await fn();
      notify.info(`${label}: submitted`);
      await tx.wait();
      notify.success(`${label}: success`);
      setReload((r) => r + 1);
    } catch (e) {
      notify.error(parseError(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="card mb-24">
        <div className="section-title">Zenova Price</div>
        <div className="kv-list mb-24">
          <div className="kv-row"><span className="kv-key">Current price (USDC per 1 ZENOVA)</span><span className="kv-val">${formatUsdc(price, 6)}</span></div>
          <div className="kv-row"><span className="kv-key">Min bound</span><span className="kv-val">{min && !min.isZero() ? '$' + formatUsdc(min, 6) : 'Disabled'}</span></div>
          <div className="kv-row"><span className="kv-key">Max bound</span><span className="kv-val">{max && !max.isZero() ? '$' + formatUsdc(max, 6) : 'Disabled'}</span></div>
          <div className="kv-row"><span className="kv-key">USDC Claim Mode</span><span className="kv-val">{claimMode ? 'ENABLED' : 'Disabled'}</span></div>
        </div>

        <div className="input-group">
          <label className="input-label">New Price (USDC)</label>
          <input className="input" type="number" step="0.000001" min="0" placeholder="0.5" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} />
        </div>
        <button
          className="btn btn-primary"
          disabled={busy === 'price' || !newPrice}
          onClick={() => runTx('price', () => adminW.setZenovaPrice(parseUsdc(newPrice)))}
        >
          {busy === 'price' && <ButtonSpinner />}Set Price
        </button>
      </div>

      <div className="card mb-24">
        <div className="section-title">Price Bounds (0 = disable)</div>
        <div className="admin-form-grid">
          <div className="input-group">
            <label className="input-label">Min (USDC)</label>
            <input className="input" type="number" placeholder="0.1" value={newMin} onChange={(e) => setNewMin(e.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Max (USDC)</label>
            <input className="input" type="number" placeholder="10" value={newMax} onChange={(e) => setNewMax(e.target.value)} />
          </div>
        </div>
        <button
          className="btn btn-secondary"
          disabled={busy === 'bounds' || newMin === '' || newMax === ''}
          onClick={() => runTx('bounds', () => adminW.setZenovaPriceBounds(
            newMin === '0' ? ethers.constants.Zero : parseUsdc(newMin),
            newMax === '0' ? ethers.constants.Zero : parseUsdc(newMax)
          ))}
        >
          {busy === 'bounds' && <ButtonSpinner />}Set Bounds
        </button>
      </div>

      <div className="card">
        <div className="section-title">Emergency USDC Claim Mode</div>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 16 }}>
          When enabled, users receive USDC directly instead of ZENOVA when claiming income.
        </p>
        <div className="flex">
          <button
            className={`btn ${claimMode ? 'btn-ghost' : 'btn-danger'}`}
            disabled={busy === 'mode-on' || claimMode}
            onClick={() => runTx('mode-on', () => adminW.setUsdcClaimMode(true))}
          >
            {busy === 'mode-on' && <ButtonSpinner />}Enable USDC Claim
          </button>
          <button
            className={`btn ${!claimMode ? 'btn-ghost' : 'btn-primary'}`}
            disabled={busy === 'mode-off' || !claimMode}
            onClick={() => runTx('mode-off', () => adminW.setUsdcClaimMode(false))}
          >
            {busy === 'mode-off' && <ButtonSpinner />}Disable (ZENOVA mode)
          </button>
        </div>
      </div>
    </div>
  );
};
